import React, { useEffect } from "react";
import { gql, useLazyQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import {
  Page,
  Card,
  Title,
  Text,
  Badge,
  ButtonRow,
  PrimaryBtn,
  SecondaryBtn,
  InfoBox,
  OrderNumber,
} from "./PlataRezultatPageStyles";

const GET_ORDER_STATUS = gql`
  query GetOrderStatus($orderNumber: String!) {
    orderStatusByNumber(orderNumber: $orderNumber) {
      order_number
      payment_status
      payment_method
      status
    }
  }
`;

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PlataRezultatPage() {
  const navigate = useNavigate();
  const query = useQuery();
  const { clearCart } = useCart();

  const authRaw = localStorage.getItem("user");
  const authUser = authRaw ? JSON.parse(authRaw) : null;
  const isLoggedIn = !!authUser?.email;

  const statusFromUrl = query.get("status") || "pending";
  const orderFromUrl = query.get("order") || query.get("orderId") || "";
  const message = query.get("message") || "";

  const orderFromStorage = localStorage.getItem("pending_card_order") || "";
  const order = orderFromUrl || orderFromStorage;

  const [getOrderStatus, { data, loading, error }] = useLazyQuery(
    GET_ORDER_STATUS,
    { fetchPolicy: "network-only" },
  );

  const realPaymentStatus = data?.orderStatusByNumber?.payment_status || "";
  const orderPaymentMethod =
    data?.orderStatusByNumber?.payment_method || query.get("method") || "";

  const isPaid = realPaymentStatus === "platit";

  const isFailed =
    statusFromUrl === "failed" ||
    statusFromUrl === "error" ||
    realPaymentStatus === "esuat" ||
    realPaymentStatus === "refuzat" ||
    realPaymentStatus === "anulat";

  const isCashOrder =
    statusFromUrl === "success" && orderPaymentMethod === "cash";

  const isSuccess = isPaid || isCashOrder;

  useEffect(() => {
    if (!order) return;
    getOrderStatus({ variables: { orderNumber: order } });
  }, [order, getOrderStatus]);

  useEffect(() => {
    if (!order || isPaid || isFailed) return;

    const interval = setInterval(() => {
      getOrderStatus({ variables: { orderNumber: order } });
    }, 2000);

    return () => clearInterval(interval);
  }, [order, isPaid, isFailed, getOrderStatus]);

  useEffect(() => {
    if (isPaid || isCashOrder) {
      clearCart();
      localStorage.removeItem("pending_card_order");
    }
  }, [isPaid, isCashOrder, clearCart]);

  if (!order) {
    return (
      <Page>
        <Card>
          <Badge $status="failed">Comandă invalidă</Badge>
          <Title>Nu am putut identifica comanda</Title>
          <Text>Te rugăm să revii în magazin și să încerci din nou.</Text>

          <ButtonRow>
            <PrimaryBtn type="button" onClick={() => navigate("/")}>
              Înapoi la magazin
            </PrimaryBtn>
          </ButtonRow>
        </Card>
      </Page>
    );
  }

  const title = isPaid
    ? "Plata a fost confirmată"
    : isCashOrder
      ? "Comanda a fost plasată cu succes"
      : isFailed
        ? "Plata nu a fost finalizată"
        : "Plata este în procesare";

  const text = isPaid
    ? "Plata comenzii tale a fost confirmată cu succes. Comanda a fost înregistrată."
    : isCashOrder
      ? "Comanda ta a fost înregistrată cu succes și va fi achitată la ridicare."
      : isFailed
        ? "Plata nu a fost finalizată. Cardul a fost refuzat, datele pot fi greșite sau fondurile sunt insuficiente."
        : "Așteptăm confirmarea plății de la NETOPIA. Dacă plata a fost anulată sau refuzată, comanda va rămâne neplătită.";

  const badgeText = isPaid
    ? "Plătită"
    : isCashOrder
      ? "Comandă înregistrată"
      : isFailed
        ? "Neplătită"
        : "În curs de confirmare";

  const badgeStatus = isSuccess ? "success" : isFailed ? "failed" : "pending";

  return (
    <Page>
      <Card>
        <Badge $status={badgeStatus}>{badgeText}</Badge>

        <Title>{title}</Title>
        <Text>{text}</Text>

        <InfoBox>
          <strong>Număr comandă:</strong>
          <OrderNumber>{order}</OrderNumber>
        </InfoBox>

        {isSuccess ? (
          <InfoBox>
            <strong>Confirmare:</strong>
            <div>Am trimis un mail de confirmare pe emailul introdus.</div>
          </InfoBox>
        ) : null}

        {message ? (
          <InfoBox>
            <strong>Detalii:</strong>
            <div>{message}</div>
          </InfoBox>
        ) : null}

        {/* {loading ? (
          <InfoBox>
            <strong>Verificare:</strong>
            <div>Verificăm statusul plății...</div>
          </InfoBox>
        ) : null} */}

        {error ? (
          <InfoBox>
            <strong>Eroare:</strong>
            <div>{error.message}</div>
          </InfoBox>
        ) : null}

        <ButtonRow>
          {isLoggedIn ? (
            <>
              <PrimaryBtn type="button" onClick={() => navigate("/comenzi")}>
                Vezi comanda
              </PrimaryBtn>

              <SecondaryBtn type="button" onClick={() => navigate("/")}>
                Înapoi la magazin
              </SecondaryBtn>
            </>
          ) : (
            <PrimaryBtn type="button" onClick={() => navigate("/")}>
              Înapoi la magazin
            </PrimaryBtn>
          )}
        </ButtonRow>
      </Card>
    </Page>
  );
}
